/**
 * Game Constants - All magic numbers extracted into named constants
 * Improves readability and maintainability
 */

// ============================================
// GAME LOOP TIMING
// ============================================
export const GAME_LOOP = {
  FAST_INTERVAL_MS: 10,        // Fast tick every 10ms (100Hz)
  SLOW_INTERVAL_MS: 100,       // Slow tick every 100ms (10Hz)
  AUTOSAVE_SLOW_TICKS: 25,     // Auto-save every 25 slow ticks (2.5 seconds)
  TUTORIAL_DELAY_MS: 1000,     // Wait 1 second before showing tutorial
} as const;

// ============================================
// INITIAL GAME STATE VALUES
// ============================================
export const INITIAL_VALUES = {
  WIRE: 1000,
  CLIPS: 0,
  FUNDS: 0,
  TRUST: 2,
  PROCESSORS: 1,
  MEMORY: 1,
  MAX_OPS: 1000,
  ACCELERATION_MULTIPLIER: 1000, // Testing mode acceleration
} as const;

// ============================================
// PRODUCTION COSTS & FORMULAS
// ============================================
export const PRODUCTION = {
  // AutoClipper
  AUTOCLIPPER_BASE_COST: 5,
  AUTOCLIPPER_COST_EXPONENT: 1.1,  // Cost = 1.1^level + 5
  AUTOCLIPPER_BASE_PRODUCTION: 0.01, // Clips per second per clipper
  
  // MegaClipper
  MEGACLIPPER_INITIAL_COST: 500,
  MEGACLIPPER_COST_BASE: 1000,
  MEGACLIPPER_COST_EXPONENT: 1.07, // Cost = 1.07^level * 1000
  
  // Wire
  WIRE_SUPPLY_PER_PURCHASE: 1000,
  WIRE_BASE_PRICE: 20,
  
  // Marketing
  MARKETING_BASE_COST: 100,
  MARKETING_COST_MULTIPLIER: 2, // Cost = 100 * 2^(level-1)
  
  // Factory
  FACTORY_BASE_COST: 100_000_000_000, // 100 billion
} as const;

// ============================================
// TRUST & MILESTONE THRESHOLDS
// ============================================
export const THRESHOLDS = {
  CLIPS_PER_TRUST_FIBONACCI: true, // Use fibonacci sequence for trust
  MILESTONE_INTERVAL: 10,          // Major milestones every 10 levels
  WIRE_LOW_THRESHOLD: 100,         // Warning threshold for low wire
  CLIP_RATE_HIGH_THRESHOLD: 1000,  // High production rate threshold
  
  // Probe costs
  PROBE_COST: 1e17, // 100 quadrillion matter
  
  // Clip burst effect threshold
  CLIP_BURST_THRESHOLD: 100, // Trigger burst when 100+ clips accumulated
  
  // Probe launch delta
  PROBE_LAUNCH_DELTA: 10, // Show effect when 10+ probes launched
  
  // Drifter combat delta
  DRIFTER_COMBAT_DELTA: 5, // Show effect when 5+ drifters destroyed
} as const;

// ============================================
// VISUAL EFFECTS PARAMETERS
// ============================================
export const VISUAL_EFFECTS = {
  // Particle counts
  STAR_PARTICLES_MAX: 100,
  PARTICLES_PER_SEGMENT: 5,
  
  // Durations (ms)
  CELEBRATION_SHAKE_DURATION: 100,
  RING_EXPANSION_DELAY: 100, // Delay between ring expansions
  ANIMATION_FADE_DURATION: 100,
  
  // Intensities
  SHAKE_INTENSITY_BASE: 0.003,
  GLITCH_INTENSITY_MIN: 0.3,
  GLITCH_INTENSITY_RANGE: 0.5,
  COLOR_SHIFT_DURATION: 200,
  COLOR_SHIFT_CREATIVITY_DURATION: 500,
  
  // Particle physics
  PARTICLE_SPEED_MIN: 0.5,
  PARTICLE_SPEED_MAX: 2,
  PARTICLE_LIFESPAN: 2000,
  PARTICLE_ALPHA_START: 0.8,
  
  // Graphics sizes (pixels)
  STAR_SIZE: 8,
  GLOW_SIZE: 32,
  SPARK_WIDTH: 8,
  SPARK_HEIGHT: 2,
} as const;

// ============================================
// CLIPPY CHARACTER BEHAVIOR
// ============================================
export const CLIPPY = {
  COMMENT_COOLDOWN_MS: 120_000,    // 2 minutes between comments
  REACTION_COOLDOWN_MS: 5_000,     // 5 seconds between reactions
  
  // Reaction probabilities
  REACTION_PROBABILITY: 0.2,        // 20% chance of reacting
  
  // Autoclick milestones
  AUTOCLICK_MILESTONE_10: 10,
  AUTOCLICK_MILESTONE_100: 100,
  
  // Milestones for commentary
  MILESTONES: [100, 500, 1000, 5000, 10000, 100_000, 1_000_000, 1e9, 1e12],
  
  // Random comment timing
  COMMENT_MIN_DELAY_MS: 120_000,    // 2 minutes minimum
  COMMENT_RANDOM_RANGE_MS: 180_000, // +0-3 minutes random
  
  // Thresholds for contextual comments
  FUNDS_THRESHOLD: 50,
} as const;

// ============================================
// TOUCH HANDLER PARAMETERS
// ============================================
export const TOUCH = {
  // Haptic patterns (ms)
  HAPTIC_LIGHT: 10,
  HAPTIC_MEDIUM: [20, 10, 20],
  HAPTIC_HEAVY: [30, 10, 30, 10, 30],
  
  // Default haptic fallback
  DEFAULT_VIBRATION_MS: 10,
  
  // Tooltip positioning offset
  TOOLTIP_OFFSET_PX: 50,
} as const;

// ============================================
// LORE SYSTEM
// ============================================
export const LORE = {
  UPDATE_INTERVAL_MS: 45_000, // Update lore text every 45 seconds
} as const;

// ============================================
// QUANTUM OPERATIONS
// ============================================
export const QUANTUM = {
  OPS_SPIKE_THRESHOLD: 500, // Trigger glitch when qOps increases by 500+
} as const;

// ============================================
// CREATIVITY
// ============================================
export const CREATIVITY = {
  SPIKE_THRESHOLD: 100, // Trigger color shift when creativity increases by 100+
} as const;

// ============================================
// STAGE PROGRESSION
// ============================================
export const STAGES = {
  MANUFACTURING: 1,
  FACTORIES: 2,
  SPACE: 3,
} as const;

// ============================================
// PRICING & SALES
// ============================================
export const PRICING = {
  DEFAULT_MARGIN: 0.25, // $0.25 default price per clip
  MIN_PRICE: 0.01,
  // Max price is dynamic based on demand
} as const;

// ============================================
// INVESTMENT ENGINE
// ============================================
export const INVESTMENT = {
  INITIAL_UPGRADE_COST: 500,
  RISK_LEVELS: {
    LOW: 0,
    MEDIUM: 1,
    HIGH: 2,
  },
} as const;

// ============================================
// TOURNAMENT SYSTEM
// ============================================
export const TOURNAMENT = {
  INITIAL_COST: 1000,
  STRATEGY_RANDOM_THRESHOLD: 0.5, // 50% probability
  RANDOM_MOVE_MAX: 10,
} as const;

// ============================================
// UI CONSTANTS
// ============================================
export const UI = {
  MOBILE_BREAKPOINT_PX: 600,
  TOAST_FADE_DURATION_MS: 100,
  
  // Settings modal dimensions
  SETTINGS_MODAL_OPACITY: {
    MOBILE: 0.5,
    DESKTOP: 0.7,
  },
  
  TUTORIAL_OVERLAY_OPACITY: {
    MOBILE: 0.5,
    DESKTOP: 0.7,
  },
  
  // Z-index layers
  Z_INDEX: {
    OVERLAY: 9998,
    HIGHLIGHTED: 9999,
    TOOLTIP: 10000,
  },
} as const;

// ============================================
// CALCULATIONS THAT COMBINE CONSTANTS
// ============================================

/**
 * Calculate AutoClipper cost for a given level
 */
export function calculateAutoClipperCost(level: number): number {
  return Math.pow(PRODUCTION.AUTOCLIPPER_COST_EXPONENT, level) + PRODUCTION.AUTOCLIPPER_BASE_COST;
}

/**
 * Calculate MegaClipper cost for a given level
 */
export function calculateMegaClipperCost(level: number): number {
  if (level === 0) return PRODUCTION.MEGACLIPPER_INITIAL_COST;
  return Math.pow(PRODUCTION.MEGACLIPPER_COST_EXPONENT, level) * PRODUCTION.MEGACLIPPER_COST_BASE;
}

/**
 * Calculate Marketing cost for a given level
 */
export function calculateMarketingCost(level: number): number {
  return PRODUCTION.MARKETING_BASE_COST * Math.pow(PRODUCTION.MARKETING_COST_MULTIPLIER, level - 1);
}

/**
 * Check if screen is mobile size
 */
export function isMobileScreen(): boolean {
  return window.innerWidth <= UI.MOBILE_BREAKPOINT_PX;
}
